/**
 * src/lib/route-bridge.tsx — URL-params → props bridge.
 *
 * The v.3 prototype's screens accept route params as React props
 * (`function GoLive({ id })`, `function MonitorDetail({ id })`). With
 * react-router-dom v6 those params live in `useParams()` instead of
 * arriving as props. To preserve the prototype's call-site shape
 * during this mechanical vendoring, the `<Bridge component={X} />`
 * wrapper reads `useParams()` and passes every URL param straight
 * through to the wrapped component.
 *
 * This is a backwards-compatibility shim; CODE-phase wiring tasks
 * are free to replace any wrapped component with `useParams()`
 * directly when they touch the screen.
 */
import { ComponentType } from 'react';
import { useParams } from 'react-router-dom';

export function Bridge<P extends Record<string, unknown>>({ component: Component }: { component: ComponentType<P> }) {
  const params = useParams();
  return <Component {...(params as unknown as P)} />;
}

export function withParams<P extends Record<string, unknown>>(Component: ComponentType<P>) {
  function BridgedComponent() {
    return <Bridge component={Component} />;
  }
  BridgedComponent.displayName = `Bridge(${Component.displayName || Component.name || 'Component'})`;
  return BridgedComponent;
}
